import React, { useState, useEffect, useRef } from 'react';
import { jsonToCSV } from '../../utils/json-to-csv';
import { exportCSV } from '../../utils/export-csv';
import { useReactToPrint } from 'react-to-print';
import ClosedSidebar from '../closedSidebar';
import OpenedSidebar from '../openedSidebar';
import Card from '../cards/ParticipantCard';
import PrinableParticipantCard from '../cards/PrintableCards/ParticipantCards';
import Pagination from '../Pagination';
import ViewParticipantInfo from '../modals/ViewParticipantInfo';
import EditParticipantInfo from '../modals/EditParticipantInfo';
import EmptyState from '../EmptyState';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function ParticipantView({ allParticipantData, allParticipantResult }) {
    const isOpened = useSelector((state) => state.sidebarState.isOpen);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [open, setOpen] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredParticipants, setFilteredParticipants] =
        useState(allParticipantData);
    const [currentData, setCurrentData] = useState([]);
    const [updateInfo, setUpdateInfo] = useState({
        updateStatus: '',
        updateData: {},
    });
    const [archiveInfo, setArchiveInfo] = useState({
        archiveStatus: '',
        archiveId: null,
    });
    const { t } = useTranslation();

    const printRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: 'Kitchen Cards',
    });

    const handleOpen = (participant) => {
        setSelectedParticipant(participant);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedParticipant(null);
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        bgcolor: 'background.paper',
        borderRadius: '20px',
        boxShadow: 24,
        outline: '0',
        p: 4,
        zIndex: 5,
    };

    useEffect(() => {
        const lowercasedFilter = searchTerm.toLowerCase();
        const filteredData = allParticipantData.filter(
            (data) =>
                data.first_name.toLowerCase().includes(lowercasedFilter) ||
                data.last_name.toLowerCase().includes(lowercasedFilter)
        );

        setFilteredParticipants(filteredData);
        setCurrentPage(1);
    }, [searchTerm, allParticipantData]);

    useEffect(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentPageData = filteredParticipants.slice(
            startIndex,
            endIndex
        );
        setCurrentData(currentPageData);
    }, [currentPage, itemsPerPage, filteredParticipants]);

    const handlePerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const totalItems = filteredParticipants.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleEditModal = () => {
        setOpen(false);
        setOpenEditModal((prev) => !prev);
    };

    const handleEditModalClose = () => {
        setOpenEditModal(false);
    };

    const handleUpdateInfo = (updateInfo) => {
        setUpdateInfo((prev) => ({
            ...prev,
            ...updateInfo,
        }));
    };

    const handleArchiveUpdate = (archiveData) => {
        setArchiveInfo((prev) => ({
            ...prev,
            ...archiveData,
        }));
    };

    // handle export csv here
    const handleExportCsv = () => {
        const participantData = allParticipantResult.allParticipantData.filter(
            (currObj) => !currObj.hasOwnProperty('tent_id')
        );
        const csvData = jsonToCSV(participantData);

        const params = {
            csvData,
            page: 'Participants',
        };
        exportCSV(params);
    };

    return (
        <main className="font-sans flex">
            {isOpened ? <OpenedSidebar /> : <ClosedSidebar />}
            <section className="w-full bg-[#F9F9F9] px-20 pt-10">
                <>
                    <h1 className="text-2xl text-tertiary font-medium">
                        {t('participants-page.Participants')}
                    </h1>
                    <div className="w-full h-20 bg-white mt-10 rounded-lg flex items-center justify-between px-5">
                        <div className="relative w-[30%]">
                            <svg
                                className="absolute right-5 top-3"
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    d="M14.1668 14.1667L17.5001 17.5M2.50012 9.16667C2.50012 10.9348 3.2025 12.6305 4.45274 13.8807C5.70299 15.131 7.39868 15.8333 9.16679 15.8333C10.9349 15.8333 12.6306 15.131 13.8808 13.8807C15.1311 12.6305 15.8335 10.9348 15.8335 9.16667C15.8335 7.39856 15.1311 5.70286 13.8808 4.45262C12.6306 3.20238 10.9349 2.5 9.16679 2.5C7.39868 2.5 5.70299 3.20238 4.45274 4.45262C3.2025 5.70286 2.50012 7.39856 2.50012 9.16667Z"
                                    stroke="#2F4858"
                                    stroke-opacity="0.6"
                                    stroke-width="1.25"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <input
                                type="text"
                                placeholder={`${t(
                                    'participants-page.Search'
                                )}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="outline-primary text-secondary bg-[#FFFFFF] border-2 border-[#EFEFEF] h-10 pl-3 rounded-full w-full"
                            />
                        </div>
                        <div className="w-[60%] flex items-center justify-end gap-5">
                            <div className="relative w-[25%] h-10">
                                <button
                                    onClick={handleExportCsv}
                                    className="w-full pr-5 h-full rounded-full bg-primary outline-none text-white font-medium text-sm"
                                >
                                    {t('participants-page.Export CSV')}
                                </button>
                            </div>
                            <div className="relative w-[18%] h-10">
                                <button
                                    onClick={handlePrint}
                                    className="w-full h-full rounded-full pr-5 bg-primary outline-none text-white font-medium text-sm"
                                >
                                    {t('participants-page.Print')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {currentData && currentData.length > 0 ? (
                        <>
                            <div className="mt-10 grid grid-cols-3 auto-rows-fr gap-4 ">
                                {currentData.map((data, index) => {
                                    if (
                                        updateInfo.updateStatus ===
                                        'participant_update_success'
                                    ) {
                                        if (
                                            data.id === updateInfo.updateData.id
                                        ) {
                                            data = {
                                                ...updateInfo.updateData,
                                            };
                                        }
                                    }

                                    return (
                                        <div
                                            className="cursor-pointer"
                                            key={index}
                                        >
                                            <Card {...data} />
                                        </div>
                                    );
                                })}
                            </div>
                            {selectedParticipant && (
                                <ViewParticipantInfo
                                    buttonLabel="Edit"
                                    handleEditParticipant={handleEditModal}
                                    handleClose={handleClose}
                                    open={open}
                                    style={style}
                                    handleArchiveUpdate={handleArchiveUpdate}
                                    participant={selectedParticipant}
                                />
                            )}
                            {selectedParticipant && (
                                <EditParticipantInfo
                                    handleClose={handleEditModalClose}
                                    style={style}
                                    open={openEditModal}
                                    participant={selectedParticipant}
                                    handleUpdateInfo={handleUpdateInfo}
                                />
                            )}
                            <Pagination
                                handlePageChange={handlePageChange}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                handlePerPageChange={handlePerPageChange}
                            />
                        </>
                    ) : (
                        <EmptyState />
                    )}
                </>
                <div style={{ display: 'none' }}>
                    <PrinableParticipantCard
                        ref={printRef}
                        data={filteredParticipants}
                    />
                </div>
            </section>
        </main>
    );
}

export default ParticipantView;
